import { genericMemo } from "@redotech/react-util/component";
import { useWindowEventListener } from "@redotech/react-util/event";
import { LoadState, useTriggerLoad } from "@redotech/react-util/load";
import { useRefSize } from "@redotech/react-util/size";
import { Input } from "@redotech/ui/form";
import classNames from "classnames";
import {
  ReactNode,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
} from "react";
import { createPortal } from "react-dom";
import { alertOnFailure } from "./alert";
import { RedoButton } from "./arbiter-components/buttons/redo-button";
import * as cardCss from "./card.module.css";
import { Flex } from "./flex";
import { Form } from "./form";
import { ActionPortalContext, Actions } from "./page";
import * as pageFormCss from "./page-form.module.css";
import { Text } from "./text";
import { OptionalTooltip } from "./tooltip/optional-tooltip";

export interface PageFormSave {
  (signal: AbortSignal): Promise<void>;
}

export const PageForm = genericMemo(function PageForm<T>({
  children,
  initial,
  input,
  save,
  additionalActions,
  bottomSave = false,
  blockQueryChanges = true,
  saveButtonHoverContentSlot,
  onReset,
}: {
  children: ReactNode | ReactNode[];
  initial: T;
  input: Input<T>;
  save: PageFormSave;
  additionalActions?: ReactNode;
  bottomSave?: boolean;
  // Whether or not to prompt the user to confirm changes on navigation attempt, when only query params change
  blockQueryChanges?: boolean;
  saveButtonHoverContentSlot?: ReactNode;
  onReset?(): void;
}) {
  const actionsPortal = useContext(ActionPortalContext);
  const id = useId();

  const [saveLoad, doSave] = useTriggerLoad((signal) =>
    alertOnFailure("Saving failed, please check your inputs")(async () => {
      await save(signal);
      return true as const;
    }),
  );

  // Useful for debugging why the form thinks it is unsaved.
  // Look for the trail of `input.changed` fields.
  useWindowEventListener(window, "dblclick", (event) => {
    if (event.altKey && (event.metaKey || event.ctrlKey)) {
      console.log("Current page form input", input);
    }
  });

  return (
    <>
      {!bottomSave && actionsPortal
        ? createPortal(
            <Actions show={input.changed}>
              <PageFormActions
                additionalActions={additionalActions}
                form={id}
                input={input}
                saveButtonHoverContentSlot={saveButtonHoverContentSlot}
                saveLoad={saveLoad}
              />
            </Actions>,
            actionsPortal,
          )
        : null}
      <Form
        blockQueryChanges={blockQueryChanges}
        id={id}
        initial={initial}
        input={input}
        onReset={onReset}
        onSubmit={doSave}
      >
        {bottomSave ? (
          <Flex dir="column" flex={1} gap="none" position="relative">
            {children}
            <PageFormBottomActions
              form={id}
              input={input}
              saveButtonHoverContentSlot={saveButtonHoverContentSlot}
              saveLoad={saveLoad}
            />
          </Flex>
        ) : (
          children
        )}
      </Form>
    </>
  );
});

const PageFormActions = genericMemo(function PageFormActions<T>({
  form,
  input,
  saveLoad,
  additionalActions,
  saveButtonHoverContentSlot,
}: {
  form: string;
  input: Input<T>;
  saveLoad: LoadState<true | undefined>;
  additionalActions?: ReactNode;
  saveButtonHoverContentSlot?: ReactNode;
}) {
  const previousErrors = useRef<string[]>([]);

  useEffect(() => {
    const currentErrorMessages = input.allErrors.map((error) =>
      error.toString(),
    );
    const previousErrorMessages = previousErrors.current;

    // Only log if errors have actually changed
    const errorsChanged =
      currentErrorMessages.length !== previousErrorMessages.length ||
      currentErrorMessages.some(
        (msg, index) => msg !== previousErrorMessages[index],
      );

    if (errorsChanged) {
      console.log("Form validation errors changed:", {
        previous: previousErrorMessages,
        current: currentErrorMessages,
        hasErrors: currentErrorMessages.length > 0,
        errorCount: currentErrorMessages.length,
      });

      // Update the previous errors reference
      previousErrors.current = currentErrorMessages;
    }
  }, [input.allErrors]);
  return (
    <div className={cardCss.buttonBar}>
      <OptionalTooltip
        meetsCondition={!!saveButtonHoverContentSlot}
        tooltipProps={{
          title: saveButtonHoverContentSlot,
          arrow: true,
          className: pageFormCss.saveButtonHoverSlotWrapper,
          componentsProps: { tooltip: { sx: { maxWidth: "none" } } },
        }}
      >
        <RedoButton
          disabled={!!input.allErrors.length || !input.changed}
          form={form}
          hierarchy="primary"
          pending={saveLoad.pending}
          size="md"
          text="Save"
          type="submit"
        />
      </OptionalTooltip>
      <RedoButton
        disabled={saveLoad.pending || !input.changed}
        form={form}
        hierarchy="secondary"
        size="md"
        text="Cancel"
        type="reset"
      />
      {additionalActions}
    </div>
  );
});

const PageFormBottomActions = genericMemo(function PageFormBottomActions<T>({
  form,
  input,
  saveLoad,
  saveButtonHoverContentSlot,
}: {
  form: string;
  input: Input<T>;
  saveLoad: LoadState<true | undefined>;
  saveButtonHoverContentSlot?: ReactNode;
}) {
  const show = input.changed || saveLoad.pending;
  const [ref, size] = useRefSize<HTMLDivElement>();

  const width = size?.width;
  const style = useMemo(
    () => ({ width: width ? `${width}px` : "100%" }),
    [width],
  );
  return (
    <>
      <div
        className={classNames(pageFormCss.spacer, { [pageFormCss.show]: show })}
        ref={ref}
      />
      <Flex
        className={classNames(pageFormCss.notificationContainer, {
          [pageFormCss.show]: show,
        })}
        justify="center"
        style={style}
      >
        <Flex
          align="center"
          aria-hidden={!show}
          className={pageFormCss.notificationBar}
          dir="row"
          flex={1}
          justify="space-between"
        >
          <Text fontSize="sm" fontWeight="regular" textColor="white">
            You have unsaved changes.
          </Text>
          <Flex gap="lg">
            <RedoButton
              className={pageFormCss.discard}
              disabled={saveLoad.pending || !input.changed}
              form={form}
              hierarchy="link"
              size="sm"
              text="Discard"
              type="reset"
            />
            <OptionalTooltip
              meetsCondition={!!saveButtonHoverContentSlot}
              tooltipProps={{
                title: saveButtonHoverContentSlot,
                arrow: true,
                className: pageFormCss.saveButtonHoverSlotWrapper,
                componentsProps: { tooltip: { sx: { maxWidth: "none" } } },
              }}
            >
              <RedoButton
                disabled={!!input.allErrors.length || !input.changed}
                form={form}
                hierarchy="primary"
                pending={saveLoad.pending}
                size="sm"
                text="Save changes"
                theme="success"
                type="submit"
              />
            </OptionalTooltip>
          </Flex>
        </Flex>
      </Flex>
    </>
  );
});
